import type { TOC } from '@ember/component/template-only';
import { Heading, Text } from 'tio-ui/components/typography';
import type { ComponentLike } from '@glint/template';
import { getEmptyStateTokens } from 'tio-reimagined/color-tokens';

// ---------------------------------------------------------------------------
// EmptyState – placeholder for empty lists or data
// ---------------------------------------------------------------------------

/**
 * Color theme options for the empty state icon
 */
export type EmptyStateColor = 'gray' | 'violet' | 'blue' | 'teal' | 'amber' | 'emerald';

interface EmptyStateSignature {
  Args: {
    /** Phosphor icon component to display */
    icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /** Title text for the empty state */
    title: string;
    /** Description text explaining why the area is empty */
    description?: string;
    /** Color theme for the icon (default: 'gray') */
    color?: EmptyStateColor;
    /** Whether to use compact sizing */
    compact?: boolean;
  };
  Blocks: {
    /** Optional action button or link */
    action?: [];
    /** Optional extra content below the description */
    extra?: [];
  };
  Element: HTMLDivElement;
}

const getColorClasses = (color?: EmptyStateColor) => getEmptyStateTokens(color);

/**
 * EmptyState - A placeholder for empty lists or data sections
 *
 * Displays a centered icon, title, description, and optional action when
 * there is no data to show. Useful for empty search results, unstarted
 * features, or pristine states.
 *
 * @example Basic usage
 * ```gts
 * import EmptyState from 'tio-reimagined/components/empty-state';
 *
 * <EmptyState
 *   @icon={{PhMagnifyingGlass}}
 *   @title="No results found"
 *   @description="Try adjusting your search or filters"
 * />
 * ```
 *
 * @example With action button
 * ```gts
 * <EmptyState @icon={{PhPlus}} @title="No applications yet" @description="Start your first application" @color="violet">
 *   <:action>
 *     <Button @intent="primary">Get Started</Button>
 *   </:action>
 * </EmptyState>
 * ```
 */
const EmptyState: TOC<EmptyStateSignature> = <template>
  {{#let (getColorClasses @color) as |colors|}}
    <div
      data-test-empty-state
      class="flex flex-col items-center justify-center text-center
        {{if @compact 'py-8 px-4' 'py-16 px-6'}}"
      ...attributes
    >
      <div
        data-test-empty-state-icon
        class="{{colors.bg}} rounded-full {{if @compact 'p-3 mb-3' 'p-4 mb-4'}}"
      >
        {{#let @icon as |Icon|}}
          <Icon @size={{if @compact 24 32}} class={{colors.text}} />
        {{/let}}
      </div>
      <Heading
        data-test-empty-state-title
        @level={{3}}
        class="font-semibold text-gray-900 dark:text-white
          {{if @compact 'text-base' 'text-lg'}}
          mb-1"
      >
        {{@title}}
      </Heading>
      {{#if @description}}
        <Text
          data-test-empty-state-description
          @intent="muted"
          class="max-w-sm {{if @compact 'text-sm' ''}}"
        >
          {{@description}}
        </Text>
      {{/if}}
      {{#if (has-block "action")}}
        <div data-test-empty-state-action class="mt-4">
          {{yield to="action"}}
        </div>
      {{/if}}
      {{#if (has-block "extra")}}
        <div class="mt-3">
          {{yield to="extra"}}
        </div>
      {{/if}}
    </div>
  {{/let}}
</template>;

export default EmptyState;

export type { EmptyStateSignature };
